import { motion } from 'framer-motion'
import ThinkinCodeFooter from '../ThinkinCodeFooter'

/**
 * Customer-facing auth frame for login / register.
 * Form content is passed as children (interaction panel).
 */
export default function AuthShell({
  children,
  eyebrow = 'Car wash & auto detailing',
  title,
  subtitle,
}) {
  return (
    <div
      className="auth-shell relative flex min-h-screen w-full flex-col overflow-x-hidden text-white"
      style={{
        ['--auth-orange']: '#f0812f',
        ['--auth-cyan']: '#5ad4ef',
        ['--auth-ink']: '#0b0c10',
      }}
    >
      {/* Atmospheric brand plane */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 90% 70% at 15% 10%, rgba(240,129,47,0.28), transparent 55%),
            radial-gradient(ellipse 70% 60% at 90% 85%, rgba(90,212,239,0.18), transparent 50%),
            radial-gradient(ellipse 50% 40% at 60% 40%, rgba(240,129,47,0.08), transparent 60%),
            linear-gradient(165deg, #08090c 0%, #12141c 45%, #0a0d12 100%)
          `,
        }}
      />
      {/* Speed-line texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(-28deg, transparent, transparent 14px, rgba(255,255,255,0.9) 14px, rgba(255,255,255,0.9) 15px)',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 75%)',
        }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-20 top-1/4 h-64 w-64 rounded-full blur-3xl"
        style={{ background: 'rgba(240,129,47,0.22)' }}
        animate={{ opacity: [0.45, 0.75, 0.45], scale: [1, 1.08, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-16 bottom-1/4 h-56 w-56 rounded-full blur-3xl"
        style={{ background: 'rgba(90,212,239,0.16)' }}
        animate={{ opacity: [0.35, 0.65, 0.35] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center gap-10 px-4 py-12 sm:px-6 lg:flex-row lg:items-center lg:gap-16 lg:px-10 lg:py-16">
        {/* Brand hero */}
        <motion.div
          className="flex flex-1 flex-col items-center text-center lg:items-start lg:text-left"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.img
            src="/brand/trendsetter-logo.png"
            alt="TrendSetter"
            className="h-28 w-auto object-contain drop-shadow-[0_12px_40px_rgba(240,129,47,0.35)] sm:h-36 lg:h-44"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />
          <p
            className="mt-6 text-xs font-semibold uppercase tracking-[0.28em]"
            style={{ color: 'var(--auth-cyan)' }}
          >
            {eyebrow}
          </p>
          <h1 className="mt-3 max-w-md text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-tight">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-3 max-w-sm text-base leading-relaxed text-white/65">{subtitle}</p>
          ) : null}
        </motion.div>

        {/* Interaction panel */}
        <motion.div
          className="w-full max-w-md shrink-0 lg:max-w-[420px]"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="rounded-2xl border border-white/10 bg-white p-6 text-foreground shadow-[0_24px_80px_-20px_rgba(0,0,0,0.55)] sm:p-8"
            style={{ boxShadow: '0 24px 80px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(90,212,239,0.12)' }}
          >
            {children}
          </div>
        </motion.div>
      </div>
      <div className="relative z-10 mt-auto">
        <ThinkinCodeFooter variant="dark" />
      </div>
    </div>
  )
}
